/**
 * The component set.
 *
 * Small on purpose. Every component here exists because two or more screens need it *and* getting
 * it wrong has a cost — a button that does not say what it does, a field with no label, a price
 * without its currency. Anything used once lives in the screen that uses it.
 */

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  RefObject,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { PRICE } from '@ksa/model'
import { useLang } from './lang.tsx'

// --- buttons ---------------------------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'quiet' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  busy?: boolean
}

/**
 * ⚠️ `type="button"` IS THE DEFAULT HERE, AND THE HTML DEFAULT IS THE OPPOSITE. A `<button>` inside a
 * `<form>` submits it unless told otherwise, so a "Delete this page" button beside a text field
 * quietly becomes a form submit — which in this application means a stray navigation in the middle
 * of editing. Every button that means to submit says so.
 *
 * ⚠️ `busy` DISABLES AND ANNOUNCES, and it keeps the label. A button whose text changes to
 * "Saving…" loses its accessible name mid-action, so a screen reader user is told the thing they
 * were on has been replaced. `aria-busy` plus an unchanged name is what actually communicates it.
 */
export function Button({ variant = 'primary', size = 'md', busy, children, className, disabled, ...rest }: ButtonProps) {
  const variantClass =
    variant === 'quiet' ? 'u-btn--quiet' : variant === 'ghost' ? 'u-btn--ghost' : variant === 'danger' ? 'u-btn--danger' : ''
  const sizeClass = size === 'lg' ? 'u-btn--lg' : size === 'sm' ? 'u-btn--sm' : ''
  return (
    <button
      type="button"
      className={`u-btn ${variantClass} ${sizeClass} ${className ?? ''}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy && <span className="u-spin" aria-hidden="true" />}
      {children}
    </button>
  )
}

// --- surfaces ---------------------------------------------------------------------------------------

export function Card({
  children,
  raised,
  className,
}: {
  children: ReactNode
  raised?: boolean
  className?: string
}) {
  return <div className={`u-card ${raised ? 'u-card--raised' : ''} ${className ?? ''}`}>{children}</div>
}

// --- fields -------------------------------------------------------------------------------------------

type FieldProps = {
  label: string
  hint?: string
  error?: string
  children: (props: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': boolean | undefined }) => ReactNode
}

/**
 * A labelled control.
 *
 * ⚠️ THE CHILD IS A FUNCTION, SO THE `id`/`aria-describedby`/`aria-invalid` WIRING CANNOT BE
 * FORGOTTEN. A `<Field>` that merely rendered a `<label>` around whatever it was given would let a
 * caller pass an input with no `id`, producing a label that points at nothing — which looks correct
 * on screen and is invisible to a screen reader. Passing the ids down as arguments makes the
 * connection the only way to use the component.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className="u-field">
      <label className="u-field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {/* The error comes BEFORE the hint in the DOM so a screen reader reads the problem first — the
          hint explains the field, the error explains why this attempt failed. */}
      {error && (
        <span className="u-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
      {hint && (
        <span className="u-field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  )
}

export function Input({ invalid, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input className={`u-input ${invalid ? 'u-input--invalid' : ''} ${className ?? ''}`} {...rest} />
}

export function Textarea({ invalid, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={`u-textarea ${invalid ? 'u-textarea--invalid' : ''} ${className ?? ''}`} {...rest} />
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`u-select ${className ?? ''}`} {...rest}>
      {children}
    </select>
  )
}

/**
 * A small closed set of choices, shown all at once.
 *
 * > ⚠️ **THIS REPLACED A BORDERLESS `<select>` THAT LASTED ONE COMMIT, AND THE REASONING FOR THAT ONE
 * > WAS RIGHT AS FAR AS IT WENT:** four bordered dropdowns in a column read as a form to be filled
 * > in, on a sheet whose whole job is to feel like touching the page. What taking the border off did
 * > not fix is that a dropdown HIDES the options — you cannot see that "another website" is even
 * > possible until you open it — and it left the caret carrying WCAG 1.4.11 on its own.
 *
 * ⚠️ RADIOS ARE FOR SETS THAT ARE SHORT AND WORTH SEEING. Three destinations, or the four pages a
 * small site has: showing them costs three lines and answers "what else could this be" without a
 * tap. This is not the control for a long list — a country, a template — where a dropdown's own
 * scrolling is the point.
 *
 * ⚠️ IT IS A REAL `<fieldset>` WITH A REAL `<legend>` AND NATIVE RADIOS SHARING A `name`. That is
 * what gets arrow-key navigation, the announced group label, and the platform's own focus ring — all
 * of which a div of buttons has to reimplement, and usually reimplements wrong. `useId` keys the
 * group, so two of these on one sheet do not become one set of six.
 */
export function Choice<T extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string
  value: T
  options: { value: T; label: ReactNode }[]
  onChange: (next: T) => void
}) {
  const name = useId()
  return (
    <fieldset className="u-choice">
      <legend className="u-field__label">{legend}</legend>
      {options.map((option) => (
        <label key={option.value} className="u-choice__option">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </fieldset>
  )
}

// --- menus -----------------------------------------------------------------------------------------------

/**
 * What a control can do that is not its main job.
 *
 * ⚠️ IT EXISTS BECAUSE THE SAME MENU WAS ABOUT TO BE WRITTEN TWICE. The editor's block menu — the ⋯
 * that duplicates and deletes a section — and a repeater row's own move/remove are the same three
 * pieces: a button, a backdrop that closes it, and a panel of items. Two copies is two places for the
 * backdrop to be forgotten, and a menu with no backdrop is one that will not close on a phone.
 *
 * ⚠️ THE ITEMS ARE DATA RATHER THAN CHILDREN, so a caller cannot put a form in a menu. Everything in
 * one of these is a verb the customer is choosing between; the moment something in here needs its own
 * layout, it did not belong in a menu.
 */
export type MenuAction = { label: string; onClick: () => void; disabled?: boolean; danger?: boolean }

/**
 * The panel half of a menu: a backdrop that closes it and a box placed beside its trigger.
 *
 * ⚠️ IT IS A PORTAL ONTO `document.body`, AND EVERY MENU IN THIS PRODUCT NEEDS THAT — which is why it
 * is a component rather than a detail of `Menu`. Two different ancestors were breaking it in two
 * different ways, and both were invisible until somebody tried to use the menu:
 *
 *   - **The drawer's body scrolls**, and a scrolling ancestor clips an absolutely-positioned panel.
 *     A repeater row's ⋯ was drawn outside the body and cut away entirely.
 *   - **The header carries `will-change: transform`** (for the slide-away), and that makes it the
 *     containing block for `position: fixed` DESCENDANTS. So the header menus' full-screen backdrop
 *     was 412×56 — the header's own box — and a tap anywhere else on the page never reached it. The
 *     menu simply would not close.
 *
 * The second one is the reason this is not solved by "use fixed instead of absolute": fixed is not
 * fixed once an ancestor says otherwise. Out of the tree entirely is the only position that cannot be
 * redefined by something above it.
 *
 * ⚠️ AND THE COORDINATES ARE MEASURED IN A LAYOUT EFFECT, so the panel is placed before the browser
 * paints. Measured in an ordinary effect it appears at 0,0 for one frame — top-left of the screen,
 * then a jump.
 */
export function MenuPanel({
  anchor,
  up,
  align = 'start',
  onClose,
  children,
}: {
  /** The trigger to place this beside. */
  anchor: RefObject<HTMLElement | null>
  /** Open upwards, for a trigger near the bottom of the screen. */
  up?: boolean
  /** Which edge of the panel lines up with the trigger's. `end` keeps a control at the far end of a
   *  row on screen; `start` is what a labelled dropdown wants. */
  align?: 'start' | 'end'
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useLang()
  const [at, setAt] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>({})

  useLayoutEffect(() => {
    const rect = anchor.current?.getBoundingClientRect()
    if (!rect) return
    const gap = 8
    /*
     * ⚠️ THE SIDE IS NAMED IN JAVASCRIPT, WHICH IS THE ONE PLACE THIS CODEBASE ALLOWS IT: a viewport
     * coordinate is a number and a number cannot mirror through a logical property. Everything else
     * about direction is a logical property and `check:logical` keeps it that way.
     */
    const rtl = document.documentElement.dir === 'rtl'
    const startsLeft = align === 'start' ? !rtl : rtl
    setAt({
      ...(up ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      ...(startsLeft ? { left: rect.left } : { right: window.innerWidth - rect.right }),
    })
  }, [anchor, up, align])

  return createPortal(
    <>
      {/*
        ⚠️ THE BACKDROP IS A BUTTON, NOT A DIV WITH A CLICK HANDLER. It is the only way out of an open
        menu on a phone, so it has to be reachable by a keyboard and announced as something that does
        anything at all. It is also why this is not a document click listener: it cannot be defeated
        by a child that stops propagation, and it stops the tap that closes the menu ALSO pressing
        whatever was underneath.
      */}
      <button className="u-menu__backdrop" aria-label={t('Close', 'إغلاق')} onClick={onClose} />
      <div className="u-menu__panel u-menu__panel--fixed" role="menu" style={at}>
        {children}
      </div>
    </>,
    document.body,
  )
}

/**
 * What a control can do that is not its main job.
 *
 * ⚠️ THE ITEMS ARE DATA RATHER THAN CHILDREN, so a caller cannot put a form in a menu. Everything in
 * one of these is a verb the customer is choosing between; the moment something in here needs its own
 * layout, it did not belong in a menu — and `MenuPanel` is there for the menus that are lists of
 * links rather than lists of verbs.
 */
export function Menu({
  label,
  actions,
  up,
}: {
  /** What the ⋯ button is called to a screen reader. There is no visible name — that is the point. */
  label: string
  actions: (MenuAction | 'rule')[]
  up?: boolean
}) {
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  return (
    <div className="u-menu">
      <button
        ref={trigger}
        type="button"
        className="u-btn u-btn--ghost u-btn--sm u-menu__button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {open && (
        <MenuPanel anchor={trigger} up={up} align="end" onClose={() => setOpen(false)}>
          {actions.map((action, i) =>
            action === 'rule' ? (
              <div key={`rule-${i}`} className="u-menu__rule" />
            ) : (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                className={`u-menu__item ${action.danger ? 'u-menu__item--bad' : ''}`}
                disabled={action.disabled}
                onClick={() => {
                  setOpen(false)
                  action.onClick()
                }}
              >
                {action.label}
              </button>
            ),
          )}
        </MenuPanel>
      )}
    </div>
  )
}

// --- notices ---------------------------------------------------------------------------------------------

/**
 * ⚠️ A NOTICE STATES SOMETHING TRUE ABOUT WHATEVER RENDERS IT, and that is a rule about *where* it is
 * used rather than about the component. CLAUDE.md §0.4: the claim belongs to whatever knows. So the
 * "payments are not real yet" sentence is rendered BY the payment component, and cannot be left
 * behind by a change somewhere else — which is precisely the failure the predecessor shipped twice,
 * with a sandbox banner sitting on a surface that had become real.
 */
export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'error'
  title?: string
  children: ReactNode
}) {
  return (
    <div className={`u-notice u-notice--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <div className="u-stack u-stack--tight">
        {title && <strong>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  )
}

export function Chip({ tone = 'draft', children }: { tone?: 'live' | 'draft' | 'attention'; children: ReactNode }) {
  return <span className={`u-chip u-chip--${tone}`}>{children}</span>
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useLang()
  return (
    <span className="u-row" role="status">
      <span className="u-spin" aria-hidden="true" />
      <span className="u-sr">{label ?? t('Loading', 'جارٍ التحميل')}</span>
    </span>
  )
}

// --- the price -------------------------------------------------------------------------------------------------

/**
 * The price, rendered the one way it is ever rendered.
 *
 * ⚠️ A COMPONENT RATHER THAN A STRING, so that the currency, the period and the VAT statement cannot
 * become separated from the number. A bare "5.00" on a page is a number a Saudi customer has to
 * guess the currency of, and a price displayed without saying it includes VAT is a compliance
 * problem rather than a design one (ZATCA requires consumer prices inclusive — see `pricing.ts`).
 *
 * The `π` is the signature and appears once. The price of this product is three riyals and fourteen
 * halalas, which is pi, and that is the single most repeatable fact about it.
 */
/*
 * > ⚠️ **`showPi` STOOD HERE AND IS DELETED WITH THE PRICE IT DECORATED.** It drew a small π beside
 * > the amount, because the amount was 3.14 ﷼. The price is 5.00 ﷼ now (`pricing.ts` says why), so
 * > the symbol decorates nothing — and a prop that no caller passed and no reader could explain is
 * > worse than a missing joke.
 */
export function Price({
  showVat = true,
  /**
   * ⚠️ `inline` EXISTS BECAUSE THE DEFAULT IS A DISPLAY SIZE AND IT LOOKED ABSURD MID-SENTENCE. The
   * price is a headline on the pricing block and a phrase inside a sentence about keeping a site
   * online; rendering the second at the first's size made a sentence with a billboard in the middle
   * of it. A `size` prop rather than a caller-supplied class, so the two sizes stay the only two and the
   * currency never drifts away from the number.
   */
  inline = false,
}: {
  showVat?: boolean
  inline?: boolean
}) {
  const { lang, t } = useLang()
  // The number and the currency are sized differently, so they are separate elements — and the
  // number comes from `Intl`'s own parts rather than from a regex over the formatted string. See
  // `amountOnly`: the regex version rendered the amount with stray stops in Arabic, because `ر.س.`
  // contains full stops of its own.
  const amount = PRICE.amountOnly(lang)

  return (
    <span className={`u-price${inline ? ' u-price--inline' : ''}`}>
      <span className="u-price__amount">{amount}</span>
      <span className="u-price__currency">{t('SAR', 'ر.س')}</span>
      <span className="u-price__per">
        {t('per site, per month', 'لكل موقع شهريًا')}
        {showVat && ` · ${t('VAT included', 'شامل الضريبة')}`}
      </span>
    </span>
  )
}

// --- empty states -------------------------------------------------------------------------------------------------

/**
 * ⚠️ AN EMPTY SCREEN IS AN INVITATION, NOT A REPORT. "No sites" tells somebody what they already
 * know; the action is what they came for. `action` is required rather than optional for that reason —
 * an empty state with nothing to do is a dead end, and making it impossible to construct one is
 * cheaper than reviewing for it.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action: ReactNode
}) {
  return (
    <div className="u-card u-stack" style={{ textAlign: 'center', paddingBlock: 'var(--u-s8)' }}>
      <h2 className="u-heading">{title}</h2>
      <p className="u-lead" style={{ maxWidth: '38rem', marginInline: 'auto' }}>
        {body}
      </p>
      <div className="u-row" style={{ justifyContent: 'center' }}>
        {action}
      </div>
    </div>
  )
}
